/**
 * @fileoverview Post Destination Controller - manages the logic for selecting location and
 * partner destinations for a post
 *
 * @author Samantha Landen (slanden@yext.com)
 */
goog.module('yext.social.post.PostDestinationController');
goog.module.declareLegacyNamespace();

const dom = goog.require('goog.dom');
const yext = goog.require('yext');
const Partner = goog.require('yext.social.post.Partner');
const PartnerSelectorView = goog.require('yext.social.post.PartnerSelectorView');
const routes = goog.require('yext.storm.routes');
const {EntityPickerController} = goog.require('yext.ui.components.entitypicker.EntityPickerController');
const {ItemTypes} = goog.require('yext.ui.components.entitypicker.container.itemtypes');

/** @constructor */
const PostDestinationController = function(options) {
  this.options = options || {};

  /** @type {!jQuery} */
  this.$ctxt = this.options.$ctxt || $('.js-post-destination-container');

  this.uepRoot = null;
  this.uepPendingSelection = {};
  this.uepFinalSelection = {};

  /** @type {PartnerSelectorView} */
  this.partnerSelector = new PartnerSelectorView({
    $el: this.$ctxt.find('.js-post-partners-container'),
    partners: this.options.partners || [],
    postablePartners: this.options.postablePartners || [],
    preferredPartners: this.options.preferredPartners || [],
    onSelectPartner: this.options.onSelectPartner,
  }).init();

  /**
   * @type {Function}
   * @private
   */
  this.beforeChange_ = null;

  /**
   * @type {Function}
   * @private
   */
  this.onChange_ = null;
};

/** @return this */
PostDestinationController.prototype.init = function() {
  if (this.options.shouldShowLocationSelector) {
    this.createAndBindEntityPicker_();
  }
  this.bindPartnerSelector_();
  return this;
};

/**
 * Binds the beforeChange_ and onChange_ callbacks to onSelect for UEP to update the available partners
 * @private
 */
PostDestinationController.prototype.createAndBindEntityPicker_ = function() {
  this.entityPicker = EntityPickerController;

  const createPostsPermissions =
    [62, 70, 73, 300, 301, 361, 98, 99, 105, 302, 303, 362];
  const socialPostsFeatures = [1030, 185];

  this.uepOptions = {
    key: false, // used to force render with new props
    locationsOnly: true,
    allowEntityTypeSelection: false,
    initialSelection: {
      'entities': this.options.locations ? this.options.locations.map(l => l.id) : [],
    },
    asFilter: true,
    applyInitialSelection: true,
    entityTypes: this.options.locationEntityTypeIds,
    alternatePermissions: [createPostsPermissions],
    additionalFeatures: [socialPostsFeatures],
    onSelect: this.onEntitySelect_.bind(this),
    rootRefCallback: root => {
      this.uepRoot = root;
    },
  };

  ReactDOM.render(
    React.createElement(EntityPickerController, this.uepOptions, null),
    dom.getRequiredElementByClass('js-post-location-container'));

  this.handleLocationScopeChange_();
};

/**
 * Binds the onChange_ callback for the partner selector
 * @private
 */
PostDestinationController.prototype.bindPartnerSelector_ = function() {
  this.partnerSelector.onChange(function(data) {
    this.handlePublisherScopeChange_();

    if (this.onChange_) {
      this.onChange_(data);
    }
  }.bind(this));

  this.handlePublisherScopeChange_();
};

/** @param {Function} cb */
PostDestinationController.prototype.beforeChange = function(cb) {
  if (!yext.isFunction(cb)) {
    throw new TypeError('PostDestinationController.beforeChange: Invalid cb, expected a function');
  }
  this.beforeChange_ = cb;
};

/** @param {Function} cb */
PostDestinationController.prototype.onChange = function(cb) {
  if (!yext.isFunction(cb)) {
    throw new TypeError('PostDestinationController.onChange: Invalid cb, expected a function');
  }
  this.onChange_ = cb;
};

/** @return {!Object<string, ?>} */
PostDestinationController.prototype.getData = function() {
  return $.extend(
    true,
    {
      'usesSelector': true,
      'locationIds': [],
      'folderIds': [],
      'locationLabelIds': [],
    },
    this.uepFinalSelection,
    this.partnerSelector.getData());
};

/**
 * Updates the controller with the selected data from the UEP component and calls before/onChange_
 * @param {Object<string, ?>} selection
 */
PostDestinationController.prototype.onEntitySelect_ = function(selection) {
  this.uepPendingSelection = {
    'locationIds': selection.entities.map(e => e.id),
    'folderIds': selection.folders.map(f => f.id),
    'locationLabelIds': selection.labels.map(l => l.id),
  };

  if (this.beforeChange_) {
    this.beforeChange_(this.uepPendingSelection);
  }

  this.handleLocationScopeChange_();
  if (this.onChange_) {
    this.onChange_(this.getData());
  }
};

/**
 * Fully clears the data in this controller
 * Called in the case of post success, post failure, or before copying information from a previous post
 */
PostDestinationController.prototype.reset = function() {
  if (this.options.shouldShowLocationSelector) {
    this.uepPendingSelection = {};
    this.uepFinalSelection = {};
    const resetEvent = new CustomEvent('uep-reset', {detail: {apply: true}});
    this.uepRoot.dispatchEvent(resetEvent);
  }

  this.partnerSelector.reset();
};

/**
 * Applies the selected (pending) locations, making them final
 * Called in beforeChange_ after confirming that the given assets are available for the selected locations
 */
PostDestinationController.prototype.applyLocationChanges = function() {
  this.uepFinalSelection = JSON.parse(JSON.stringify(this.uepPendingSelection));
  this.uepPendingSelection = {};
};

/**
 * Resets the UEP back to the previous set of selected locations
 * Called in beforeChange_ if the user chooses to cancel their current (pending) selection
 * due to the assets not being available for the selected locations
 */
PostDestinationController.prototype.resetLocationSelector = function() {
  if (!this.options.shouldShowLocationSelector) return;

  this.uepPendingSelection = {};
  const applySelectionEvent = new CustomEvent('uep-apply-selection', {
    detail: {
      apply: true,
      selection: {
        'entities': this.uepFinalSelection.locationIds,
        'folders': this.uepFinalSelection.folderIds,
        'labels': this.uepFinalSelection.locationLabelIds,
      },
    },
  });
  this.uepRoot.dispatchEvent(applySelectionEvent);
};

/**
 * Updates the destinations to those provided
 * @param {!Object<string, ?>} destinations
 */
PostDestinationController.prototype.populate = function(destinations) {
  if (this.options.shouldShowLocationSelector) {
    this.uepFinalSelection = {
      'locationIds': destinations['locations']
        ? destinations['locations'].map(l => l['id']) : destinations['locationIds'],
    };

    const applySelectionEvent = new CustomEvent('uep-apply-selection', {
      detail: {
        apply: true,
        selection: {
          'entities': this.uepFinalSelection['locationIds'],
        },
      },
    });
    this.uepRoot.dispatchEvent(applySelectionEvent);
  }

  this.partnerSelector.setSelectedPartners(destinations['partnerIds'] || []);
};

/**
 * Retrieves the appropriate partners based on the newly selected locations/folders/labels
 */
PostDestinationController.prototype.handleLocationScopeChange_ = function() {
  var scope = this.uepFinalSelection;

  var jQxhr;
  if (scope['folderIds'] && scope['folderIds'].length > 0) {
    jQxhr = $.ajax({
      url: routes.SocialPost.getPartnersForFolders({
        'folderIds': scope['folderIds'],
      }),
      type: 'GET',
    });
  } else if (scope['locationIds'] && scope['locationIds'].length > 0) {
    jQxhr =$.ajax({
      url: routes.SocialPost.getPartnersForLocations({
        'locationIds': scope['locationIds'],
      }),
      type: 'GET',
    });
  } else if (scope['locationLabelIds'] && scope['locationLabelIds'].length > 0) {
    jQxhr = $.ajax({
      url: routes.SocialPost.getPartnersForLabels({
        'labelIds': scope['locationLabelIds'],
      }),
      type: 'GET',
    });
  } else {
    var availablePartners = this.removeTwitterForMultiLocs_(
      this.partnerSelector.getAllAvailablePartnerIds());
    this.partnerSelector.setAvailablePartners({
      'partners': availablePartners,
      'hasSocialWidgets': availablePartners.includes(Partner.PartnerId.WIDGETS),
    });
    return;
  }

  jQxhr.done(function(data) {
    this.partnerSelector.setAvailablePartners(Object.assign({}, data, {
      'partners': this.removeTwitterForMultiLocs_(data['partners']),
    }));
  }.bind(this))
    .fail(function() {
    // If we couldn't load the available partners, mark all of them as available and deal with it
    // when submitting the post
      this.partnerSelector.setAvailablePartners(null);
    }.bind(this));
};

/**
 * @private
 * @param {!Array<number>} publisherIds
 * @return the publisherIds that with Twitter removed if >1 location is selected
 */
PostDestinationController.prototype.removeTwitterForMultiLocs_ =
function(publisherIds) {
  var scope = this.uepFinalSelection;
  var hasLocationIds = scope['locationIds'] && scope['locationIds'].length > 0;

  var accuratePublisherIds = publisherIds.slice();
  if (!hasLocationIds || scope['locationIds'].length > 1) {
    var twitterIdx = publisherIds.indexOf(Partner.PartnerId.TWITTER);
    if (twitterIdx > -1) {
      accuratePublisherIds.splice(twitterIdx, 1);
    }
  }

  return accuratePublisherIds;
};

/**
 * Toggles multiselect based on whether or not Twitter is an available partner for the selected location(s)
 */
PostDestinationController.prototype.handlePublisherScopeChange_ = function() {
  if (!this.options.shouldShowLocationSelector) return;

  var publisherIds = this.partnerSelector.getData()['partnerIds'] || [];
  if (publisherIds.includes(Partner.PartnerId.TWITTER)) {
    // if twitter is included, then there should only be one location selected
    this.uepOptions.singleSelect = true;
    this.uepOptions.initialSelection = {
      'entities': this.uepFinalSelection['locationIds'] || [],
    };
  } else {
    this.uepOptions.singleSelect = false;
    this.uepOptions.initialSelection = {
      'entities': this.uepFinalSelection['locationIds'] || [],
      'folders': this.uepFinalSelection['folderIds'] || [],
      'labels': this.uepFinalSelection['locationLabelIds'] || [],
    };
  }

  // making a new key forces React to make a new component with updated props
  this.uepOptions.key = this.uepOptions.singleSelect;

  ReactDOM.render(
    React.createElement(EntityPickerController, this.uepOptions, null),
    dom.getRequiredElementByClass('js-post-location-container'));
};

exports = PostDestinationController;
